import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { api, mxn, fmtDate, formatApiError, exportToExcel } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Modal, Btn, Select, Input, EmptyState, ConfirmDialog, ListSkeleton } from "@/components/Kit";
import { StatusBadge, FolioTag } from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { LineItemsEditor } from "@/components/LineItemsEditor";
import { TagsField, TagsCell } from "@/components/TagsInput";
import { ReviewModal } from "@/components/ReviewModal";
import { PrintRemisionDialog } from "@/components/PrintRemisionDialog";
import { ImportDialog } from "@/components/ImportDialog";
import { FolioSelector } from "@/components/FolioSelector";
import { FilterBar, matchFilters, sortByFolio, FolioSortHeader } from "@/components/FilterBar";
import { useRowSelection, SelectCheckbox } from "@/components/BulkSelect";
import { usePagination, Pagination } from "@/components/Pagination";
import { IconBtn } from "@/pages/Cotizaciones";
import { toast } from "sonner";
import { Plus, Trash2, Printer, Wrench, Pencil, Upload, Link2, Download, Copy, CircleSlash } from "lucide-react";

const FACT_BADGE = {
  facturada: { label: "Facturada", cls: "bg-[#49AC9A]/15 text-[#49AC9A] border-[#49AC9A]/30" },
  pendiente: { label: "Pendiente por facturar", cls: "bg-[#FDC50D]/15 text-[#FDC50D] border-[#FDC50D]/30" },
  no_aplica: { label: "No requiere factura", cls: "bg-stone-200 text-stone-600 border-stone-300" },
};

function FacturacionBadge({ r }) {
  if (r.status === "Anulado" || r.anulado) {
    return <div data-testid={`fact-estado-${r.number}`}><StatusBadge status="Anulado" /></div>;
  }
  const st = FACT_BADGE[r.estado_facturacion] || FACT_BADGE.no_aplica;
  return (
    <div data-testid={`fact-estado-${r.number}`}>
      <span className={`inline-flex items-center border px-2 py-0.5 text-xs uppercase tracking-wider font-mono-data ${st.cls}`}>
        {st.label}
      </span>
      {r.estado_facturacion === "facturada" && (
        <div className="font-mono-data text-xs text-[#5B6672] mt-1">
          {r.factura_number} · {fmtDate(r.fecha_facturacion)}
        </div>
      )}
    </div>
  );
}

export default function Remisiones() {
  const navigate = useNavigate();
  const { canDo } = useAuth();
  const rCreate = canDo("remisiones", "create");
  const rEdit = canDo("remisiones", "edit");
  const rConvert = canDo("remisiones", "convert");
  const rCancel = canDo("remisiones", "cancel");
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [remisiones, setRemisiones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [clientId, setClientId] = useState("");
  const [clientName, setClientName] = useState("");
  const [items, setItems] = useState([{ description: "", quantity: 1, unit_price: 0 }]);
  const [ivaRate, setIvaRate] = useState(16);
  const [notes, setNotes] = useState("");
  const [tags, setTags] = useState([]);
  const [number, setNumber] = useState("");
  const [requiereFactura, setRequiereFactura] = useState(false);
  const [relateQuote, setRelateQuote] = useState(true);
  const [quoteId, setQuoteId] = useState("");
  const [quoteSearch, setQuoteSearch] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(null);
  const [reviewDoc, setReviewDoc] = useState(null);
  const [reviewType, setReviewType] = useState("orden");
  const [filters, setFilters] = useState({});
  const [folioDir, setFolioDir] = useState("desc");
  const [printRemisionId, setPrintRemisionId] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [convertRem, setConvertRem] = useState(null);
  const [convertSel, setConvertSel] = useState(new Set());
  const [converting, setConverting] = useState(false);

  const [linkRem, setLinkRem] = useState(null);
  const [linkableInvoices, setLinkableInvoices] = useState([]);
  const [linkInvSel, setLinkInvSel] = useState("");
  const [linking, setLinking] = useState(false);
  const [linkAllClients, setLinkAllClients] = useState(false);

  const [folioOpts, setFolioOpts] = useState(null);
  const [folioSel, setFolioSel] = useState("");
  const [notifyUsers, setNotifyUsers] = useState([]);
  const [notifyEmail, setNotifyEmail] = useState("");
  const loadFolioOptions = () => {
    setFolioSel("");
    setFolioOpts(null);
    api.get("/folios/options?prefix=REM").then((r) => setFolioOpts(r.data)).catch(() => setFolioOpts(null));
  };

  const load = () => {
    api.get("/remisiones").then((r) => setRemisiones(r.data)).finally(() => setLoading(false));
    api.get("/clients").then((r) => setClients([...r.data].sort((a, b) => (a.name || "").localeCompare(b.name || "", "es", { sensitivity: "base" }))));
    api.get("/products?active=true").then((r) => setProducts(r.data));
    api.get("/quotes").then((r) => setQuotes(r.data));
    api.get("/notices/users").then((r) => setNotifyUsers(r.data)).catch(() => setNotifyUsers([]));
  };
  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const fq = location.state?.fromQuote;
    if (fq) {
      openNewFromQuote(fq);
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  useEffect(() => {
    const qid = searchParams.get("fromQuote");
    if (qid && quotes.length) {
      const q = quotes.find((x) => x.id === qid);
      if (q) {
        openNewFromQuote(q);
        setSearchParams({}, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, quotes]);

  useEffect(() => {
    const eid = searchParams.get("edit");
    const did = searchParams.get("duplicate");
    const target = eid || did;
    if (target && remisiones.length) {
      const r = remisiones.find((x) => x.id === target);
      if (r) {
        eid ? openEdit(r) : openDuplicate(r);
        setSearchParams({}, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, remisiones]);

  const filteredRemisiones = useMemo(
    () => sortByFolio(remisiones.filter((r) => matchFilters(r, filters)), folioDir),
    [remisiones, filters, folioDir]
  );

  const sel = useRowSelection(filteredRemisiones);
  const pageRems = usePagination(filteredRemisiones, 50, { persist: true });

  const exportExcel = async () => {
    try {
      const FACT_LABEL = { facturada: "Facturada", pendiente: "Pendiente por facturar", no_aplica: "No requiere factura" };
      const headers = ["Folio", "Cliente", "Fecha", "Tags", "Total", "Facturación"];
      const rows = filteredRemisiones.map((r) => [
        r.number, r.client?.name || "", fmtDate(r.created_at), (r.tags || []).join(", "),
        Number(r.total || 0), FACT_LABEL[r.estado_facturacion] || "No requiere factura",
      ]);
      const by = {};
      let tc = 0, tt = 0;
      filteredRemisiones.forEach((r) => {
        const s = FACT_LABEL[r.estado_facturacion] || "No requiere factura";
        by[s] = by[s] || { c: 0, t: 0 };
        by[s].c++; by[s].t += Number(r.total || 0);
        tc++; tt += Number(r.total || 0);
      });
      const summaryRows = Object.entries(by).map(([s, v]) => [s, v.c, Number(v.t.toFixed(2))]);
      summaryRows.push(["TOTAL GENERAL", tc, Number(tt.toFixed(2))]);
      const summary = { title: "Resumen", headers: ["Facturación", "Cantidad", "Total"], rows: summaryRows };
      await exportToExcel("remisiones", "Remisiones", headers, rows, summary);
    } catch {
      toast.error("No se pudo exportar a Excel");
    }
  };

  const clientOptions = useMemo(
    () => [...clients].sort((a, b) => (a.name || "").localeCompare(b.name || "", "es", { sensitivity: "base" })),
    [clients]
  );

  const filteredQuotes = useMemo(() => {
    const q = quoteSearch.trim().toLowerCase();
    const list = [...quotes].sort((a, b) => (b.number || "").localeCompare(a.number || "", "es"));
    if (!q) return list.slice(0, 50);
    return list
      .filter((x) =>
        (x.number || "").toLowerCase().includes(q) ||
        (x.client?.name || "").toLowerCase().includes(q)
      )
      .slice(0, 50);
  }, [quotes, quoteSearch]);

  const selectedQuote = useMemo(() => quotes.find((x) => x.id === quoteId) || null, [quotes, quoteId]);

  const openLinkRem = async (r) => {
    setLinkRem(r);
    setLinkInvSel("");
    setLinkAllClients(false);
    setLinkableInvoices([]);
    fetchLinkableInvoices(r.id, false);
  };

  const fetchLinkableInvoices = async (rid, all) => {
    try {
      const { data } = await api.get(`/remisiones/${rid}/linkable-invoices${all ? "?all=true" : ""}`);
      setLinkableInvoices(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const toggleLinkAllClients = (v) => {
    setLinkAllClients(v);
    setLinkInvSel("");
    if (linkRem) fetchLinkableInvoices(linkRem.id, v);
  };

  const confirmLinkRem = async () => {
    if (!linkInvSel) return toast.error("Selecciona una factura");
    setLinking(true);
    try {
      await api.post(`/invoices/${linkInvSel}/link-remisiones`, { remision_ids: [linkRem.id] });
      const inv = linkableInvoices.find((i) => i.id === linkInvSel);
      toast.success(`Remisión ${linkRem.number} relacionada con la factura ${inv?.number || ""}`);
      setLinkRem(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLinking(false);
    }
  };

  const createProduct = async (payload) => {    try {
      const { data } = await api.post("/products", payload);
      setProducts((prev) => [data, ...prev]);
      toast.success("Producto creado");
      return data;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      return null;
    }
  };

  const openNew = () => {
    setEditing(null);
    setClientId(clients[0]?.id || "__new__");
    setClientName("");
    setItems([{ description: "", quantity: 1, unit_price: 0 }]);
    setIvaRate(16);
    setNotes("");
    setTags([]);
    setNumber("");
    setRequiereFactura(false);
    setRelateQuote(true);
    setQuoteId("");
    setQuoteSearch("");
    setNotifyEmail("");
    loadFolioOptions();
    setOpen(true);
  };

  const openNewFromQuote = (q) => {
    setEditing(null);
    setClientId(q.client_id || "__new__");
    setClientName(q.client_id ? "" : (q.client_name || q.client?.name || ""));
    setItems(q.items && q.items.length ? q.items.map((i) => ({ ...i })) : [{ description: "", quantity: 1, unit_price: 0 }]);
    setIvaRate(q.iva_rate ?? 16);
    setNotes(q.notes || "");
    setTags([...(q.tags || [])]);
    setNumber("");
    setRequiereFactura(false);
    setRelateQuote(true);
    setQuoteId(q.id);
    setQuoteSearch("");
    setNotifyEmail("");
    loadFolioOptions();
    setOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r.id);
    setClientId(r.client_id || "__new__");
    setClientName(r.client_id ? "" : (r.client_name || r.client?.name || ""));
    setItems(r.items.length ? r.items : [{ description: "", quantity: 1, unit_price: 0 }]);
    setIvaRate(r.iva_rate);
    setNotes(r.notes || "");
    setTags(r.tags || []);
    setNumber(r.number || "");
    setRequiereFactura(!!r.requiere_factura);
    setRelateQuote(!!r.quote_id);
    setQuoteId(r.quote_id || "");
    setQuoteSearch("");
    setNotifyEmail(r.notify_email || "");
    setOpen(true);
  };

  const openDuplicate = (r) => {
    setEditing(null);
    setClientId(r.client_id || "__new__");
    setClientName(r.client_id ? "" : (r.client_name || r.client?.name || ""));
    setItems(r.items && r.items.length ? r.items.map((i) => ({ ...i })) : [{ description: "", quantity: 1, unit_price: 0 }]);
    setIvaRate(r.iva_rate ?? 16);
    setNotes(r.notes || "");
    setTags([...(r.tags || [])]);
    setNumber("");
    setRequiereFactura(!!r.requiere_factura);
    setRelateQuote(!!r.quote_id);
    setQuoteId(r.quote_id || "");
    setQuoteSearch("");
    setNotifyEmail("");
    loadFolioOptions();
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!editing) {
      const nextNum = folioOpts?.next?.number || "";
      if (folioSel && folioSel !== nextNum) {
        return toast.error(`El folio ${folioSel} no es el consecutivo correcto. El folio debe ser exactamente el siguiente en la secuencia: ${nextNum || "—"}.`);
      }
    }
    const isNew = clientId === "__new__";
    if (isNew && !clientName.trim()) return toast.error("Escribe el nombre del cliente");
    if (!isNew && !clientId) return toast.error("Selecciona un cliente");
    if (!tags || tags.length === 0) return toast.error("Agrega al menos un tag (etiqueta) para guardar el documento");
    if (!notifyEmail) return toast.error("Selecciona a quién notificar (aviso crítico)");
    const payload = {
      client_id: isNew ? null : clientId,
      client_name: isNew ? clientName.trim() : "",
      items, iva_rate: ivaRate, notes,
      requiere_factura: requiereFactura,
      quote_id: relateQuote ? (quoteId || null) : null,
      tags,
      notify_email: notifyEmail,
      ...(editing ? { number } : { folio: folioSel || null }),
    };
    try {
      if (editing) {
        await api.put(`/remisiones/${editing}`, payload);
        toast.success("Remisión actualizada");
      } else {
        const { data } = await api.post("/remisiones", payload);
        if (folioSel && data?.number && data.number !== folioSel) {
          toast.info(`El folio ${folioSel} ya se ocupó; se asignó ${data.number}`);
        } else {
          toast.success("Remisión creada");
        }
        setOpen(false);
        if (data?.id) return navigate(`/documento/remision/${data.id}`);
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const openConvert = (r) => {
    navigate(`/ordenes?fromRemision=${r.id}`);
  };
  const toggleConvertItem = (i) =>
    setConvertSel((prev) => {
      const n = new Set(prev);
      n.has(i) ? n.delete(i) : n.add(i);
      return n;
    });
  const doConvert = async () => {
    if (!convertRem) return;
    const idxs = [...convertSel];
    if (idxs.length === 0) return toast.error("Selecciona al menos un concepto");
    setConverting(true);
    try {
      const { data } = await api.post(`/remisiones/${convertRem.id}/convert-to-order`, { item_indices: idxs });
      toast.success("Orden de trabajo creada");
      setConvertRem(null);
      setReviewType("orden");
      setReviewDoc(data);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setConverting(false);
    }
  };

  const remove = async () => {
    if (!deleteOpen) return;
    await api.delete(`/remisiones/${deleteOpen.id}`);
    toast.success(`Remisión ${deleteOpen.number} eliminada`);
    setDeleteOpen(null);
    load();
  };

  const bulkRemove = async () => {
    try {
      await api.post(`/remisiones/bulk-delete`, { ids: sel.selectedIds });
      toast.success(`${sel.selectedIds.length} remisiones eliminadas`);
      sel.clear();
      setBulkOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <>
      <PageHeader
        overline="Ciclo de venta · Paso 2"
        title="Remisiones"
        action={
          <div className="flex gap-3">
            <Btn variant="outline" onClick={exportExcel} data-testid="export-remisiones-button" disabled={remisiones.length === 0}>
              <Download size={16} /> Exportar a Excel
            </Btn>
            {rCreate && (
              <Btn variant="outline" onClick={() => setImportOpen(true)} data-testid="import-remisiones-button">
                <Upload size={16} /> Importar
              </Btn>
            )}
            {rCreate && <Btn onClick={openNew} data-testid="new-remision-button"><Plus size={16} /> Nueva remisión</Btn>}
          </div>
        }
      />
      <div className="p-10">
        {loading ? (
          <ListSkeleton testid="remisiones-loading" />
        ) : remisiones.length === 0 ? (
          <EmptyState
            title="Sin remisiones aún"
            subtitle="Convierte una cotización aprobada en remisión, o crea una directamente. Desde la remisión decides cuándo facturar."
            action={rCreate && clients.length > 0 && <Btn onClick={openNew}><Plus size={16} /> Nueva remisión</Btn>}
          />
        ) : (
          <>
          <FilterBar clients={clients} reportType="remisiones" onApply={setFilters} statuses={[...new Set(remisiones.map((r) => r.status).filter(Boolean))].sort((a, b) => a.localeCompare(b, "es"))} showTags />
          {rCancel && sel.selectedIds.length > 0 && (
            <div className="flex items-center justify-between border border-[#E3E8EE] border-b-0 bg-[#F4F1EC] px-4 py-2.5" data-testid="remisiones-bulk-bar">
              <span className="font-mono-data text-sm text-[#1C1A17]">
                {sel.selectedIds.length} seleccionada(s)
              </span>
              <div className="flex gap-2">
                <Btn variant="danger" onClick={() => setBulkOpen(true)} data-testid="bulk-delete-remisiones">
                  <CircleSlash size={15} /> Anular seleccionadas
                </Btn>
              </div>
            </div>
          )}
          <div className="border border-[#E3E8EE] bg-white data-scroll">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#E3E8EE] text-left">
                  <Th className="w-10">{rCancel && <SelectCheckbox checked={sel.allChecked} onChange={sel.toggleAll} testid="select-all-remisiones" />}</Th>
                  <Th><FolioSortHeader dir={folioDir} onToggle={() => setFolioDir((d) => d === "desc" ? "asc" : "desc")} /></Th><Th>Cliente</Th><Th>Fecha</Th><Th>Tags</Th>
                  <Th className="text-right">Total</Th><Th>OT</Th><Th>Facturación</Th><Th className="text-right">Acciones</Th>
                </tr>
              </thead>
              <tbody>
                {pageRems.pageItems.map((r) => (
                  <tr key={r.id} data-testid={`remision-row-${r.number}`} onClick={() => navigate(`/documento/remision/${r.id}`)} className="border-b border-[#E3E8EE] last:border-0 hover:bg-[#E9EEF3] transition-colors cursor-pointer">
                    <Td onClick={(e) => e.stopPropagation()}>{rCancel && <SelectCheckbox checked={sel.selected.has(r.id)} onChange={() => sel.toggle(r.id)} testid={`select-remision-${r.number}`} />}</Td>
                    <Td className="font-medium"><FolioTag number={r.number} kind="remision" testid={`remision-folio-${r.number}`} /></Td>
                    <Td>{r.client?.name || "—"}</Td>
                    <Td>{fmtDate(r.created_at)}</Td>
                    <Td><TagsCell tags={r.tags} testid={`remision-tags-${r.number}`} /></Td>
                    <Td className="text-right text-[#16202B]">{mxn(r.total)}</Td>
                    <Td data-testid={`remision-ot-${r.number}`}>
                      {r.ot_numbers && r.ot_numbers.length
                        ? <span className="inline-flex flex-wrap gap-1">{r.ot_numbers.map((n) => <FolioTag key={n} number={n} kind="ot" />)}</span>
                        : "—"}
                    </Td>
                    <Td><FacturacionBadge r={r} /></Td>
                    <Td className="text-right whitespace-nowrap">
                      <div onClick={(e) => e.stopPropagation()}>
                      {rConvert && ((r.status === "Anulado" || r.anulado) ? (
                        <span title="No se puede generar OT de una remisión anulada." className="inline-block" data-testid={`order-remision-anulada-${r.number}`}>
                          <IconBtn disabled testid={`order-remision-${r.number}`}><Wrench size={15} /></IconBtn>
                        </span>
                      ) : r.ot_numbers && r.ot_numbers.length > 0 ? (
                        <span title={`Esta remisión ya tiene la Orden de Trabajo ${r.ot_numbers.join(", ")} asociada`} className="inline-block" data-testid={`order-remision-disabled-${r.number}`}>
                          <IconBtn disabled testid={`order-remision-${r.number}`}><Wrench size={15} /></IconBtn>
                        </span>
                      ) : (
                        <IconBtn title="Generar orden de trabajo" onClick={() => openConvert(r)} testid={`order-remision-${r.number}`}><Wrench size={15} /></IconBtn>
                      ))}
                      {rConvert && r.requiere_factura && !r.invoiced && r.estado_facturacion !== "facturada" && (
                        <IconBtn title="Relacionar con factura" onClick={() => openLinkRem(r)} testid={`link-remision-${r.number}`}><Link2 size={15} /></IconBtn>
                      )}
                      <IconBtn title="PDF" onClick={() => setPrintRemisionId(r.id)} testid={`print-remision-${r.number}`}><Printer size={15} /></IconBtn>
                      {rCreate && <IconBtn title="Duplicar" onClick={() => openDuplicate(r)} testid={`duplicate-remision-${r.number}`}><Copy size={15} /></IconBtn>}
                      {rEdit && <IconBtn title="Editar" onClick={() => openEdit(r)} testid={`edit-remision-${r.number}`}><Pencil size={15} /></IconBtn>}
                      {rCancel && <IconBtn title="Anular Documento" tone="anular" onClick={() => setDeleteOpen(r)} testid={`delete-remision-${r.number}`}><CircleSlash size={15} /></IconBtn>}
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination {...pageRems} testid="pagination-remisiones" />
          </>
        )}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Editar remisión" : "Nueva remisión"} xl logo>
        <form onSubmit={save} className="space-y-5">
          {!editing && <FolioSelector options={folioOpts} value={folioSel} onChange={setFolioSel} />}
          <Select label="Cliente *" value={clientId} onChange={(e) => setClientId(e.target.value)} testid="remision-client-select">
            <option value="__new__">➕ Cliente nuevo (sin registrar)</option>
            {clientOptions.map((c) => <option key={c.id} value={c.id}>{c.name}{c.company ? ` — ${c.company}` : ""}</option>)}
          </Select>
          <div className="border border-[#ED1F26]/30 bg-[#ED1F26]/5 p-3">
            <Select label="Notificar a (aviso crítico) *" value={notifyEmail} onChange={(e) => setNotifyEmail(e.target.value)} testid="remision-notify-select">
              <option value="">Selecciona un usuario…</option>
              {notifyUsers.map((u) => <option key={u.email} value={u.email}>{u.name}</option>)}
            </Select>
            <p className="font-mono-data text-xs text-[#5B6672] mt-1.5">
              El usuario recibirá un aviso inmediato en pantalla que deberá confirmar de enterado.
            </p>
          </div>
          {clientId === "__new__" && (
            <div className="border border-[#465B7F]/30 bg-[#465B7F]/5 p-4">
              <Input label="Nombre del cliente nuevo *" value={clientName} onChange={(e) => setClientName(e.target.value)} testid="remision-client-name" placeholder="Ej. Comercializadora del Norte" />
              <p className="font-mono-data text-xs text-[#5B6672] mt-2">
                Al guardar la remisión, este cliente se dará de alta automáticamente (si aún no existe).
              </p>
            </div>
          )}
          {editing && (
            <Input label="Número de remisión *" value={number} onChange={(e) => setNumber(e.target.value)} testid="remision-number" required />
          )}
          <div>
            <span className="overline block mb-2">Cotización relacionada</span>
            <div className="grid grid-cols-2 gap-3" data-testid="remision-relate-quote">
              {[
                { v: true, t: "Relacionar con cotización", d: "Vincula esta remisión a una cotización existente.", rec: true },
                { v: false, t: "Sin cotización relacionada", d: "Crea la remisión sin vincularla a ninguna cotización.", rec: false },
              ].map((o) => (
                <label key={String(o.v)} data-testid={`remision-relate-${o.v ? "si" : "no"}`}
                  className={`flex gap-3 p-3 border cursor-pointer transition-colors ${relateQuote === o.v ? "border-[#465B7F] bg-[#465B7F]/5" : "border-[#E3E8EE] hover:border-[#465B7F]/50"}`}>
                  <input type="radio" name="relate-quote" checked={relateQuote === o.v}
                    onChange={() => { setRelateQuote(o.v); if (!o.v) { setQuoteId(""); setQuoteSearch(""); } }}
                    className="mt-0.5 accent-[#465B7F]" />
                  <span className="flex-1">
                    <span className="flex items-center gap-2">
                      <span className="font-mono-data text-sm text-[#1C1A17]">{o.t}</span>
                      {o.rec && (
                        <span className="inline-flex items-center bg-[#49AC9A]/15 text-[#49AC9A] border border-[#49AC9A]/30 px-1.5 py-0.5 text-[10px] uppercase tracking-wider font-mono-data">
                          Recomendado
                        </span>
                      )}
                    </span>
                    <span className="block font-mono-data text-xs text-[#5B6672] mt-0.5">{o.d}</span>
                  </span>
                </label>
              ))}
            </div>
            {relateQuote && (
              <div className="mt-3 border border-[#E3E8EE] p-3">
                <Input
                  label="Buscar cotización (por folio o cliente)"
                  value={quoteSearch}
                  onChange={(e) => setQuoteSearch(e.target.value)}
                  testid="remision-quote-search"
                  placeholder="Ej. COT-0123 o nombre del cliente"
                />
                {selectedQuote && (
                  <div className="flex items-center justify-between mt-2 bg-[#465B7F]/5 border border-[#465B7F]/30 px-3 py-2" data-testid="remision-selected-quote">
                    <span className="font-mono-data text-sm text-[#16202B]">
                      {selectedQuote.number} · {selectedQuote.client?.name || "—"} · {mxn(selectedQuote.total)}
                    </span>
                    <button type="button" onClick={() => { setQuoteId(""); }} data-testid="remision-quote-clear"
                      className="text-[#5B6672] hover:text-[#ED1F26]"><Trash2 size={14} /></button>
                  </div>
                )}
                <div className="mt-2 border border-[#E3E8EE] divide-y divide-[#E3E8EE] max-h-52 overflow-auto" data-testid="remision-quote-list">
                  {filteredQuotes.length === 0 ? (
                    <p className="font-mono-data text-xs text-[#5B6672] px-3 py-4 text-center">Sin cotizaciones que coincidan.</p>
                  ) : filteredQuotes.map((q) => (
                    <label key={q.id} data-testid={`remision-quote-option-${q.number}`}
                      className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-[#E9EEF3]">
                      <input type="radio" name="remision-quote" checked={quoteId === q.id} onChange={() => setQuoteId(q.id)}
                        className="h-4 w-4 accent-[#465B7F]" />
                      <span className="font-mono-data text-sm text-[#16202B] w-28">{q.number}</span>
                      <span className="font-mono-data text-xs text-[#5B6672] flex-1 truncate">{q.client?.name || "—"}</span>
                      <span className="font-mono-data text-xs text-[#5B6672]">{fmtDate(q.created_at)}</span>
                      <span className="font-mono-data text-sm text-[#16202B]">{mxn(q.total)}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <p className="font-mono-data text-xs text-[#5B6672] mt-2">
              Relacionar la remisión con una cotización es <b>recomendable</b> para mantener la trazabilidad del pedido, pero <b>no es obligatorio</b>: puedes guardar la remisión sin cotización relacionada.
            </p>
          </div>
          <div>
            <span className="overline block mb-2">¿Requiere factura?</span>
            <div className="flex gap-3" data-testid="remision-requiere-factura">
              {[{ v: true, t: "Sí, se facturará" }, { v: false, t: "No requiere factura" }].map((o) => (
                <label key={String(o.v)} data-testid={`remision-requiere-${o.v ? "si" : "no"}`}
                  className={`flex items-center gap-2 px-4 py-2.5 border cursor-pointer transition-colors ${requiereFactura === o.v ? "border-[#465B7F] bg-[#465B7F]/5" : "border-[#E3E8EE] hover:border-[#465B7F]/50"}`}>
                  <input type="radio" name="requiere-factura" checked={requiereFactura === o.v}
                    onChange={() => setRequiereFactura(o.v)} className="accent-[#465B7F]" />
                  <span className="font-mono-data text-sm text-[#1C1A17]">{o.t}</span>
                </label>
              ))}
            </div>
          </div>
          <LineItemsEditor items={items} setItems={setItems} ivaRate={ivaRate} setIvaRate={setIvaRate} products={products} onCreateProduct={createProduct} />
          <label className="block">
            <span className="overline block mb-1.5">Notas</span>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="remision-notes"
              className="w-full bg-white border border-[#E3E8EE] px-3 py-2 font-mono-data text-sm focus:outline-none focus:ring-1 focus:ring-[#465B7F]" />
          </label>
          <TagsField tags={tags} setTags={setTags} testid="remision-tags-input" />
          <div className="flex justify-end gap-3">
            <Btn variant="ghost" type="button" onClick={() => setOpen(false)}>Cancelar</Btn>
            <Btn type="submit" data-testid="save-remision-button">Guardar remisión</Btn>
          </div>
        </form>
      </Modal>

      <ConfirmDialog open={!!deleteOpen} onClose={() => setDeleteOpen(null)} onConfirm={remove}
        title="Anular remisión" message={`¿Anular la remisión ${deleteOpen?.number}? Cambiará a estatus "Anulado" y conservará su folio (no se elimina).`}
        confirmLabel="Anular remisión" testid="confirm-delete-remision-button" />

      <ConfirmDialog open={bulkOpen} onClose={() => setBulkOpen(false)} onConfirm={bulkRemove}
        title="Anular remisiones" message={`¿Anular ${sel.selectedIds.length} remisión(es)? Cambiarán a estatus "Anulado" y conservarán su folio.`}
        confirmLabel="Anular seleccionados" testid="confirm-bulk-delete-remisiones-button" />

      <Modal open={!!convertRem} onClose={() => setConvertRem(null)} title={`Generar orden desde ${convertRem?.number || ""}`} wide>
        <div className="space-y-4">
          <p className="font-mono-data text-sm text-[#5B6672]">
            Selecciona los conceptos que incluirá la orden de trabajo. Si ya existe una orden para esta remisión, solo podrás crear otra como <span className="text-[#16202B]">cumplimiento parcial</span> (menos conceptos que el total).
          </p>
          <div className="border border-[#E3E8EE] divide-y divide-[#E3E8EE]" data-testid="convert-items-list">
            {(convertRem?.items || []).map((it, i) => (
              <label key={i} data-testid={`convert-item-${i}`}
                className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-[#E9EEF3]">
                <input type="checkbox" checked={convertSel.has(i)} onChange={() => toggleConvertItem(i)}
                  data-testid={`convert-item-check-${i}`} className="h-4 w-4 accent-[#465B7F]" />
                <span className="flex-1 font-mono-data text-sm text-[#16202B]">{it.description}</span>
                <span className="font-mono-data text-xs text-[#5B6672]">Cant: {it.quantity}</span>
              </label>
            ))}
          </div>
          <div className="font-mono-data text-xs text-[#5B6672]">
            {convertSel.size} de {(convertRem?.items || []).length} conceptos seleccionados
          </div>
          <div className="flex justify-end gap-3">
            <Btn variant="ghost" type="button" onClick={() => setConvertRem(null)}>Cancelar</Btn>
            <Btn type="button" onClick={doConvert} disabled={converting} data-testid="confirm-convert-order-button">
              {converting ? "Generando…" : "Generar orden"}
            </Btn>
          </div>
        </div>
      </Modal>

      <Modal open={!!linkRem} onClose={() => setLinkRem(null)} title={`Relacionar remisión · ${linkRem?.number || ""}`} wide>
        <div className="space-y-4">
          <p className="font-mono-data text-sm text-[#5B6672]">
            Selecciona una <span className="text-[#16202B]">factura existente</span> del cliente <span className="text-[#16202B]">{linkRem?.client?.name || ""}</span> para relacionar esta remisión ({mxn(linkRem?.total || 0)}). La remisión quedará marcada como <span className="text-[#16202B]">Facturada</span>.
          </p>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={linkAllClients} onChange={(e) => toggleLinkAllClients(e.target.checked)}
              data-testid="link-invoices-all-clients" className="w-4 h-4 accent-[#465B7F]" />
            <span className="font-mono-data text-xs text-[#5B6672]">Mostrar facturas de todos los clientes</span>
          </label>
          {linkableInvoices.length === 0 ? (
            <p className="font-mono-data text-sm text-[#5C5A55] py-6 text-center border border-[#E3E8EE]" data-testid="no-linkable-invoices">
              No hay facturas disponibles{linkAllClients ? "" : " para este cliente"}.
            </p>
          ) : (
            <div className="border border-[#E3E8EE] divide-y divide-[#E3E8EE] max-h-72 overflow-auto" data-testid="linkable-invoices-list">
              {linkableInvoices.map((inv) => (
                <label key={inv.id} data-testid={`linkable-invoice-${inv.number}`}
                  className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-[#E9EEF3]">
                  <input type="radio" name="linkable-invoice" checked={linkInvSel === inv.id} onChange={() => setLinkInvSel(inv.id)}
                    data-testid={`linkable-invoice-check-${inv.number}`} className="h-4 w-4 accent-[#465B7F]" />
                  <span className="font-mono-data text-sm text-[#16202B] w-28">{inv.number}</span>
                  <span className="font-mono-data text-xs text-[#5B6672] flex-1 truncate">{inv.client?.name || "—"}</span>
                  <span className="font-mono-data text-xs text-[#5B6672]">{fmtDate(inv.created_at)}</span>
                  <span className="font-mono-data text-sm text-[#16202B]">{mxn(inv.total)}</span>
                </label>
              ))}
            </div>
          )}
          <div className="flex justify-end gap-3">
            <Btn variant="ghost" type="button" onClick={() => setLinkRem(null)}>Cancelar</Btn>
            <Btn type="button" onClick={confirmLinkRem} disabled={linking || !linkInvSel} data-testid="confirm-link-remision-button">
              {linking ? "Relacionando…" : "Relacionar"}
            </Btn>
          </div>
        </div>
      </Modal>

      <ReviewModal
        open={!!reviewDoc}
        type={reviewType}
        doc={reviewDoc}
        clients={clients}
        products={products}
        onCreateProduct={createProduct}
        onClose={() => { setReviewDoc(null); load(); }}
        onAccepted={load}
      />

      <PrintRemisionDialog open={!!printRemisionId} onClose={() => setPrintRemisionId(null)} remisionId={printRemisionId} />

      <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} module="remisiones" title="Importar remisiones" onDone={load} />
    </>
  );
}

function Th({ children, className = "" }) {
  return <th className={`overline font-normal px-4 py-3 ${className}`}>{children}</th>;
}
function Td({ children, className = "", onClick }) {
  return <td onClick={onClick} className={`font-mono-data text-sm text-[#5B6672] px-4 py-3 ${className}`}>{children}</td>;
}
